<template>
  <div class="sms-not-received mt-3">
    <p class="subtitle">Essa é mais uma medida de segurança para garantir que você é a única pessoa acessando sua conta Mercafacil.</p>
    <p v-if="hasPhone" class="sms-text">
      Disparamos um SMS com um código de validação para <strong>{{ hiddenPhone || phone }}</strong
      >, para confirmar seu nº de telefone, basta inserir o código recebido abaixo:
    </p>
    <p v-else class="sms-text">Informe um nº de telefone que será usado para enviar um o código de confirmação:</p>

    <template v-if="hasPhone">
      <div class="text-center mb-4" @click="openPhoneUpdate">
        <span class="py-2 px-4 link">Esse não é meu telefone</span>
      </div>
      <pin-input :error="pinError" @send-code="handleUserPin($event)" />
    </template>
    <template v-else>
      <v-text-field
        v-model="phone"
        v-mask="['+55 (##) #####-####', '+55 (##) ####-####']"
        :error-messages="phoneError"
        :hide-details="false"
        label="Insira seu número de telefone"
        outlined
        :required="isPhoneRequired"
        @input="onPhoneInput"
        @keydown.enter="sendCode"
      />
    </template>

    <turnstile-widget
      ref="turnstileRef"
      class="mt-3"
      :sitekey="turnstileSitekey"
      @error="handleTurnstileError"
      @reset="handleTurnstileReset"
      @verified="handleTurnstileVerified"
    />

    <v-btn v-if="hasPhone" block class="btn-submit-form mt-3" color="primary" :disabled="loading || !canSubmitCode" :loading="loading" @click="handleSubmit">
      Verificar código
    </v-btn>

    <v-btn v-else block class="btn-submit-form mt-3" color="primary" :disabled="loading || !canSendCode" :loading="loading" @click="sendCode">
      Enviar código
    </v-btn>

    <template v-if="!invalidSender && hasPhone">
      <div>
        <div v-if="step !== 'dont-received-sms'" class="text-center mt-2">
          <span class="py-2 px-4 link" :class="disabledTimer ? 'disabled-timer' : ''" @click="resendCode">Redisparar SMS {{ secondsText }}</span>
        </div>
        <div v-if="step !== 'dont-received-sms'" class="text-center mt-2 mb-0">
          <span
            class="py-2 px-4 link"
            @click="
              step = 'dont-received-sms'
              invalidSender = true
            "
            >Não recebi meu SMS</span
          >
        </div>
      </div>
    </template>

    <div v-if="invalidSender" class="d-flex justify-center">
      <div>
        <p class="mb-4">
          Entre em contato com o administrador da sua plataforma para conferir seus dados e solicitar a atualização do seu acesso pela plataforma Mercafacil.
        </p>
        <p>Em último caso entre em contato com: atendimeto@mercafacil.com</p>
        <div class="link-container mt-6 text-center">
          <span
            class="link"
            @click="
              step = 'pin-input'
              invalidSender = false
            "
            >Recebi meu SMS</span
          >
        </div>
      </div>
    </div>

    <div v-if="hiddenPhone && !hasPhone" class="text-center">
      <v-btn class="mt-8 unset-capitalize" color="marca-0-l40" text @click="backToPinInput">
        <mf-icon class="mr-2" color="marca-0-l40" fill size="20">arrow_back</mf-icon>
        Voltar
      </v-btn>
    </div>
    <back-to-login v-else @go-back="goBack" />
  </div>
</template>

<script>
import config from '@/config'
import { pinInput } from '@/modules/login/mixins/pin-input'
import { MUTATION_RESEND_PHONE_VALIDATION, MUTATION_SEND_PHONE_VALIDATION } from '@/modules/login/graphql'
import { maxLength, minLength, required } from '@vuelidate/validators'
import { notOnlySpace } from '@/helpers/validators'

export default {
  name: 'TwoFactorAuthentication',
  components: {
    PinInput: () => import('@/components/PinInput.vue'),
    BackToLogin: () => import('@/components/BackToLogin.vue'),
    TurnstileWidget: () => import('@/components/TurnstileWidget.vue'),
  },
  mixins: [pinInput],
  props: {
    token: {
      type: String,
      default: '',
    },
    pinErr: {
      type: String,
      default: '',
    },
    hiddenPhone: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    step: 'pin-input',
    turnstileSitekey: config.cloudflare.turnstile.sitekey,
    code: '',
    preventSendCode: false,
    turnstileToken: '',
    time: null,
    invalidSender: false,
    hasPhone: false,
    timeout: null,
  }),
  computed: {
    isPhoneRequired() {
      return !this.hasPhone
    },
    isPhoneValid() {
      return this.phone && this.phone.length >= 18
    },
    canSubmitCode() {
      return this.turnstileToken && this.code.length === 6 && !this.preventSendCode
    },
    canSendCode() {
      return this.turnstileToken && this.isPhoneValid && !this.loading
    },
    dontVerifyAgain: {
      get() {
        return this.dontVerifyDeviceAgain
      },
      set(value) {
        this.$emit('update:dontVerifyDeviceAgain', value)
      },
    },
  },
  watch: {
    pinErr(value) {
      this.pinError = value
    },
    hiddenPhone(value) {
      this.phone = value
      this.hasPhone = !!value
    },
  },
  created() {
    this.phone = this.hiddenPhone
    this.hasPhone = !!this.hiddenPhone
  },
  methods: {
    backToPinInput() {
      this.hasPhone = true
      this.$refs.turnstileRef?.reset()
    },
    onPhoneInput() {
      this.wrongPhone = false
    },
    openPhoneUpdate() {
      this.phone = ''
      this.$refs.turnstileRef?.reset()
      this.hasPhone = false
    },
    enabledResend() {
      this.$refs.turnstileRef?.reset()
    },
    dontReceivedEmail() {
      this.step = 'dont-received-email'
      if (!this.phone) {
        this.invalidSender = true
      }
    },
    async sendCode() {
      this.v$.$validate()
      if (this.v$?.$error) return
      if (this.wrongPhone) return
      if (!this.turnstileToken) return
      this.pinError = ''
      this.loading = true

      try {
        await this.$apollo.mutate({
          mutation: MUTATION_SEND_PHONE_VALIDATION,
          fetchPolicy: 'no-cache',
          variables: {
            token: this.token,
            phone: this.phone,
          },
          context: {
            headers: {
              fingerprint: this.fingerprint,
              'turnstile-token': this.turnstileToken,
            },
          },
        })
        this.hasPhone = true
        this.v$.$reset()
        this.$refs.turnstileRef.reset()
      } catch (error) {
        this.$refs.turnstileRef.reset()
        console.log('resendCode', error)
        this.$emit('error', error)
      } finally {
        this.loading = false
        this.turnstileToken = ''
      }
    },
    async resendCode() {
      if (this.disabledTimer) return
      this.pinError = ''

      if (!this.turnstileToken) {
        this.$snackbar({ message: 'Obrigatório Confirmar que é humano.', snackbarColor: 'error' })
        return
      }

      this.loading = true

      try {
        await this.$apollo.mutate({
          mutation: MUTATION_RESEND_PHONE_VALIDATION,
          fetchPolicy: 'no-cache',
          variables: {
            token: this.token,
          },
          context: {
            headers: {
              fingerprint: this.fingerprint,
              'turnstile-token': this.turnstileToken,
            },
          },
        })

        this.startTimer()
      } catch (error) {
        console.log('resendCode', error)
        this.$emit('error', error)
      } finally {
        this.loading = false
        this.$refs.turnstileRef.reset()
        this.turnstileToken = ''
      }
    },
    goBack() {
      this.$emit('go-back')
    },
    async handleTurnstileVerified(token) {
      this.turnstileToken = token
    },
    async handleTurnstileReset() {
      this.turnstileToken = ''
    },
    async handleTurnstileError(errorCode) {
      console.log('handleTurnstileError', errorCode)
    },
    handleUserPin(code) {
      this.code = code
    },
    handleSubmit() {
      this.$emit('remove-error')
      if (!this.turnstileToken) return
      if (this.code.length < 6) return
      if (this.preventSendCode) return
      this.preventSendCode = true
      this.time = setTimeout(() => {
        this.$emit('send-code', { code: this.code, turnstileToken: this.turnstileToken })
        this.preventSendCode = false
      }, 300)
    },
    trySms() {
      this.phone = this.hiddenPhone
      clearTimeout(this.time)
      this.step = 'try-sms'
      this.resendCode()
    },
  },
  validations() {
    return {
      phone: { required, notOnlySpace, minLength: minLength(18), maxLength: maxLength(19) },
    }
  },
}
</script>

<style lang="scss" scoped>
.unset-capitalize {
  text-transform: unset !important;
  letter-spacing: 0.02px !important;
}
.sms-not-received {
  & p {
    font-size: 16px;
    line-height: 120%;
    text-align: center;
    font-weight: 400;
  }
  & .subtitle {
    color: #4b525a;
  }

  & strong {
    color: #00b395;
  }

  & .sms-text {
    color: #6b7580;
  }

  & .link-container {
    text-align: center;
  }
  & ::v-deep label {
    color: #4b525a;
    font-size: 14px;
  }
}
</style>
